import { CardShell } from '@atlas/ui-components'
import { Avatar, Chip, cn } from '@atlas/ui-primitives'
import { Clock } from 'lucide-react'
import type { BoardWorkflow, WorkOrder } from '@/domain/work-order.types'
import { combinedComplexityCode, isTerminalStatus, WorkOrderSource } from '@/domain/work-order.types'
import { formatDate } from '@/lib/format-date'

/**
 * Time-until-due SLA label from the `dueAt` datetime. Granularity adapts to the
 * distance: hours within ~2 days (`26h left`, `5h overdue`), days beyond that
 * (matches the validated board). `now` is injectable for deterministic tests.
 */
function slaLabel(
	wo: WorkOrder,
	isTerminal: boolean,
	now: number = Date.now(),
): { text: string; tone: 'muted' | 'warning' | 'danger' } {
	if (isTerminal) return { text: 'Delivered', tone: 'muted' }
	if (!wo.dueAt) return { text: 'No due date', tone: 'muted' }
	const ms = new Date(wo.dueAt).getTime() - now
	const hours = Math.round(ms / 3_600_000)
	const days = Math.round(ms / 86_400_000)
	if (ms < 0) {
		const overdueH = Math.abs(hours)
		return { text: overdueH < 48 ? `${overdueH}h overdue` : `${Math.abs(days)}d overdue`, tone: 'danger' }
	}
	if (hours <= 48) return { text: `${hours}h left`, tone: 'warning' }
	if (days <= 7) return { text: `${days}d left`, tone: 'muted' }
	return { text: `Due ${formatDate(wo.dueAt)}`, tone: 'muted' }
}

/** First value of an axis in the tuple (for the brand/geo display chips). Tolerates
 * a missing tuple — the list DTO may omit it (only the detail endpoint hydrates it). */
const firstTag = (wo: WorkOrder, axis: string): string | undefined => wo.tuple?.[axis]?.[0]

/** SLA tone → text-colour token. */
const SLA_TONE_CLASS: Record<'muted' | 'warning' | 'danger', string> = {
	danger: 'text-ds-danger-strong',
	warning: 'text-ds-warning',
	muted: 'text-ds-text-subtle',
}

/** Compact "changed Nh/Nd ago" from the last status change. */
function statusAge(iso: string, now: number = Date.now()): string {
	const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000))
	if (mins < 60) return `${mins}m ago`
	const hours = Math.round(mins / 60)
	if (hours < 48) return `${hours}h ago`
	return `${Math.round(hours / 24)}d ago`
}

export interface WorkOrderCardProps {
	wo: WorkOrder
	/** The parent order's human code (`WO-1042`), resolved by the board from its list. */
	parentOrderCode?: string
	/**
	 * The page's workflow grouping model, used to resolve whether `wo` sits in a
	 * terminal status. Omit only where no workflow context is available (e.g. an
	 * isolated story) — `isTerminalStatus` falls back to the legacy `done` code.
	 */
	workflows?: BoardWorkflow[]
}

/** The order's number and status in the external system it came from; nothing for a manual order. */
function ExternalLine({ number, status }: { number: string | null; status: string | null }) {
	if (!number && !status) return null
	return (
		<div className="mt-1.5 flex items-center gap-1.5 text-ds-2xs">
			{number ? <span className="font-mono text-ds-text-subtle">#{number}</span> : null}
			{status ? (
				<Chip variant="secondary" size="sm">
					{status}
				</Chip>
			) : null}
		</div>
	)
}

/** How many children a parent has; nothing for an order without any. */
function ChildrenCount({ count }: { count: number | undefined }) {
	if (!count) return null
	return (
		<p className="mt-1 font-mono text-ds-2xs text-ds-text-subtle">
			{count} {count === 1 ? 'child' : 'children'}
		</p>
	)
}

/**
 * Board card for a work order. Purely presentational — the board wraps it in a
 * draggable/clickable shell (drag to change status, click to open detail).
 * Shows the orderCode, derived complexity code (L4E3), SLA, tuple chips,
 * source badge, In-Review substate, and the parent link; for an order from an
 * external system its number and status there, and for a parent its children count.
 */
export function WorkOrderCard({ wo, parentOrderCode, workflows = [] }: WorkOrderCardProps) {
	const isTerminal = isTerminalStatus(wo, workflows)
	const sla = slaLabel(wo, isTerminal)
	const slaTone = SLA_TONE_CLASS[sla.tone]
	const complexity = combinedComplexityCode(wo)
	const brand = firstTag(wo, 'brand')
	const geography = firstTag(wo, 'geography')

	return (
		<CardShell as="article" className="p-3.5 shadow-ds-sm transition-shadow hover:shadow-ds-md">
			<div className="flex items-center gap-2">
				<span className="font-mono text-xs text-ds-text-subtle">{wo.orderCode}</span>
				{complexity ? (
					<Chip variant="muted" size="sm" title={`${wo.complexityLevel ?? ''} · ${wo.assetTypeCode ?? ''}`.trim()}>
						{complexity}
					</Chip>
				) : null}
				<Chip variant={wo.source === WorkOrderSource.WORKFRONT ? 'secondary' : 'muted'} size="sm" className="ml-auto">
					{wo.source === WorkOrderSource.WORKFRONT ? 'Workfront' : 'Manual'}
				</Chip>
			</div>

			<ExternalLine number={wo.externalReferenceNumber} status={wo.externalStatus} />

			<h3 className="mt-2 text-sm font-semibold leading-snug text-ds-text">{wo.title}</h3>

			{/* Parent order link (sub-order/variant) — design shows "↳ parent WO-XXXX". */}
			{parentOrderCode ? (
				<p className="mt-1 font-mono text-ds-2xs text-ds-text-subtle">↳ parent {parentOrderCode}</p>
			) : null}
			<ChildrenCount count={wo.childrenCount} />

			{/* Tuple chips — only when set (DEC-16: empty = all, so nothing to show). */}
			{brand || geography ? (
				<div className="mt-2.5 flex flex-wrap items-center gap-1.5">
					{brand ? (
						<Chip variant="muted" size="sm">
							{brand}
						</Chip>
					) : null}
					{geography ? (
						<Chip variant="muted" size="sm">
							{geography}
						</Chip>
					) : null}
				</div>
			) : null}

			<div className="mt-3 flex items-center justify-between">
				<span className={cn('flex items-center gap-1 text-xs font-medium', slaTone)}>
					<Clock className="h-3.5 w-3.5" strokeWidth={1.75} />
					{sla.text}
					<span className="ml-1 font-normal text-ds-text-subtle">· {statusAge(wo.statusChangedAt)}</span>
				</span>
				<Avatar
					size="sm"
					fallback={wo.assigneeIds?.length ? String(wo.assigneeIds.length) : '—'}
					className="h-6 w-6 bg-ds-surface text-ds-3xs text-ds-text-muted"
					title={wo.assigneeIds?.length ? `${wo.assigneeIds.length} assignee(s)` : 'Unassigned'}
				/>
			</div>
		</CardShell>
	)
}
